<template>
  <div class="form-group">
    <InputNumberSetting
      labelTitle="Votes per participant"
      unit="votes"
      :min="MIN_VOTE_IDEAS"
      :max="MAX_VOTE_IDEAS"
      tooltip="The max number of votes each participant can give out"
      @updateSetting="updateSettingSlide(2)"
      v-if="slideActive.type === 'ideas'"
    />

    <!-- RESULT SETTINGS -->
    <div class="pb-4 border-t border-base-30" v-if="isShowResultDivider"></div>
    <LockSubmissionConfig :updateSettingSlide="updateSettingSlide" />
    <div class="pb-4 border-t border-base-30" v-if="isShowResultDivider"></div>
    <div class="result-settings-wrapper">
      <label class="mb-3" v-if="isResultSettingsVisible">
        <aha-icon name="poll" class="settings-icon mb-0.5 mr-1"></aha-icon>{{ $i18nPresenter.t('Results') }}
      </label>
      <ResultVisibilitySettings :updateSettingSlide="updateSettingSlide" />
    </div>

    <ChartTypes v-if="isChartLayoutsVisible" />
    <OpenEndedLayout v-if="isLayoutOpenEndedVisible" />

    <div
      class="pb-3 border-t border-base-30"
      v-if="isOtherSettingsDividerVisible"
    >
    </div>
    <!-- OTHER SETTINGS -->
    <label
      class="label-option"
      v-if="isOtherSettingsLabelVisible"
    >{{ $i18nPresenter.t('Other settings') }}</label>

    <AhaInfoBox v-if="slideActive.type === 'question'" class="mb-2">
      {{ $i18nPresenter.t('You can do Q&A with your audience during your presentation without having a designated Q&A slide. On the top menu, go to Settings > Q&A to access the settings.') }}
    </AhaInfoBox>

    <ScalesOtherSettings
      v-if="slideActive.type === 'scale'"
      @updateSettingSlide="updateSettingSlide"
    />
    <div
      class="form-check form-check-inline"
      v-if="isShowShowImageTick"
    >
      <div class="custom-control custom-checkbox">
        <input
          type="checkbox"
          class="custom-control-input"
          id="check-show-image-question"
          v-model="slideActive.showQuestionImage"
          @change="updateSettingSlide(10)"
        />
        <label class="custom-control-label" for="check-show-image-question">{{ $i18nPresenter.t('Show image') }}</label>
        <span class="pl-1 title-table-presentation-tooltip tooltip-on-hover">
          <b-button
            v-tooltip="$i18nPresenter.t('Toggle between showing the image and the result')"
            class="tooltip-btn"
          />
          <i class="fas fa-question-circle" />
        </span>
      </div>
    </div>
    <div class="form-check form-check-inline" v-if="slideActive.type == 'bulletPoints'">
      <div class="custom-control custom-checkbox">
        <input
          type="checkbox"
          class="custom-control-input"
          id="check-show-all-bulletpoint"
          v-model="slideActive.showAllBulletPoints"
          @change="updateSettingSlide(7)"
        />
        <label
          class="custom-control-label"
          for="check-show-all-bulletpoint"
        >{{ $i18nPresenter.t('Show all bullet points at once') }}</label>
        <span class="pl-1 title-table-presentation-tooltip">
          <b-button
            v-tooltip="$i18nPresenter.t('Show all points at once instead of one by one')"
            class="tooltip-btn"
          />
          <i class="fas fa-question-circle" />
        </span>
      </div>
    </div>

    <!-- Allow audience to submit more than once -->
    <div class="form-check form-check-inline" v-if="multipleSubmissionVisible">
      <div class="custom-control custom-checkbox">
        <input
          type="checkbox"
          class="custom-control-input"
          id="check-multiple-openended"
          @change="updateSettingSlide(6)"
          v-model="slideActive.openEndedMultipleOption"
        />
        <label class="custom-control-label" for="check-multiple-openended">
          {{ $i18nPresenter.t('Allow audience to submit more than once') }}
        </label>
        <span class="pl-1 title-table-presentation-tooltip">
          <b-button v-tooltip="openEndedMultipleOptionTooltip" class="tooltip-btn" />
          <i class="fas fa-question-circle" />
        </span>
      </div>
    </div>

    <!-- Image submission -->
    <div
      class="form-check form-check-inline hide-result-form result-settings-option"
      ref="form-check-image-submission"
      v-if="isImageSubmissionVisible"
    >
      <div class="custom-control custom-checkbox">
        <input
          type="checkbox"
          class="custom-control-input"
          id="check-image-submission"
          v-model="slideActive.imageSubmission"
          @change="updateSettingSlide(18)"
        />
        <label class="custom-control-label" for="check-image-submission">
          {{ $i18nPresenter.t('Allow audience to submit images') }}
        </label>
        <span class="pl-1 pr-3 title-table-presentation-tooltip">
          <b-button v-tooltip="imageSubmissionTooltip" class="tooltip-btn" />
          <i class="fas fa-question-circle" />
        </span>
      </div>
    </div>

    <!-- 17. Profanity filter -->
    <div
      class="form-check form-check-inline"
      v-if="profanityFilterVisible
        & slideActive.type !== 'question' && slideActive.type !== 'pickAnswer'"
    >
      <div class="custom-control custom-checkbox">
        <input
          type="checkbox"
          class="custom-control-input"
          id="check-profanity-filter"
          v-model="$store.state.presentation.filteringProfanity"
          @change="handleUpdateFilteringProfanity()"
          :disabled="isDisableWhenNotOwner"
        />
        <label class="custom-control-label" for="check-profanity-filter">
          {{ $i18nPresenter.t('Filter profanity') }}
        </label>
        <span class="pl-1 title-table-presentation-tooltip">
          <b-button
            v-tooltip="$i18nPresenter.t('Hide swear words by the audience (in their submissions and nicknames)')"
            class="tooltip-btn"
          />
          <i class="fas fa-question-circle" />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import Socket from '@/socket'
import utils from '@/util'
import EventBus from '@/eventBus'
import eventCategories from '@/constant/eventCategories'
import constants from '@/constant'
import SlideApi from '@/api/slide'
import PresentationApi from '@/api/presentation'
import cookie from '@/services/cookie'

// components
import InputNumberSetting from '@/components-v2/presentation-editor/side-right/InputNumberSetting.vue'
import ChartTypes from '@/components/presentations/ChartTypes.vue'
import OpenEndedLayout from '@/components/presentations/layouts/OpenEndedLayout.vue'
import ResultVisibilitySettings from '@/components-v2/presentation-editor/side-right/ResultVisibilitySettings.vue'
import LockSubmissionConfig from '@/components-v2/presentation-editor/side-right/LockSubmissionConfig.vue'
import ScalesOtherSettings from '@/components-v2/presentation-editor/side-right/ScalesOtherSettings.vue'

export default {
  components: {
    InputNumberSetting,
    ChartTypes,
    OpenEndedLayout,
    ResultVisibilitySettings,
    LockSubmissionConfig,
    ScalesOtherSettings,
  },
  data() {
    return {
      MAX_VOTE_IDEAS: constants.MAX_VOTE_IDEAS,
      MIN_VOTE_IDEAS: constants.MIN_VOTE_IDEAS,
      showQuizInfoTop: true,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
    }),
    ...mapGetters(['getSlideActive', 'isDisableWhenNotOwner']),
    isShowResultDivider() {
      return this.slideActive.type !== 'pickAnswer' &&
        this.slideActive.type !== 'leaderboard' &&
        this.slideActive.type !== 'ideas' &&
        this.slideActive.type !== 'question' &&
        this.slideActive.type !== 'bulletPoints'
    },
    isResultSettingsVisible() {
      const slideTypes = ['multipleChoice', 'imageChoice', 'openEnded', 'wordCloud', 'scale']
      return slideTypes.includes(this.slideActive.type)
    },
    isChartLayoutsVisible() {
      const slideTypes = ['multipleChoice', 'imageChoice']
      return slideTypes.includes(this.slideActive.type)
    },
    isLayoutOpenEndedVisible() {
      return this.slideActive.type === 'openEnded'
    },
    isOtherSettingsDividerVisible() {
      return this.isOtherSettingsLabelVisible && (this.slideActive.type === 'multipleChoice' ||
        this.slideActive.type === 'imageChoice' ||
        this.slideActive.type === 'openEnded' ||
        this.slideActive.type === 'wordCloud' ||
        this.slideActive.type === 'scale')
    },
    isOtherSettingsLabelVisible() {
      const slideTypes = ['multipleChoice', 'imageChoice', 'leaderboard', 'pickAnswer']
      return !slideTypes.includes(this.slideActive.type)
    },
    multipleSubmissionVisible() {
      const slideTypes = ['openEnded', 'wordCloud', 'ideas']
      return slideTypes.includes(this.slideActive.type)
    },
    openEndedMultipleOptionTooltip() {
      if (this.slideActive.type === 'openEnded' || this.slideActive.type === 'ideas') {
        return this.$i18nPresenter.t('Offer the possibility for participants to submit input several times from each device')
      }
      return this.$i18nPresenter.t('Let each participant submit many times')
    },
    isImageSubmissionVisible() {
      return this.slideActive.type === 'openEnded' && !this.isEURegion
    },
    imageSubmissionTooltip() {
      if (this.isEducationUser) {
        return this.$i18nPresenter.t('When checked, students can attach an image to their submission')
      }
      return this.$i18nPresenter.t('When checked, participants can attach an image to their submission')
    },
    isEducationUser() {
      return this.currentUser && this.currentUser.personalInformation &&
        this.currentUser.personalInformation.useAhaSlidesFor &&
        this.currentUser.personalInformation.useAhaSlidesFor === 'education'
    },
    profanityFilterVisible() {
      const slideTypes = ['openEnded', 'wordCloud', 'question', 'pickAnswer', 'leaderboard', 'ideas']
      return slideTypes.includes(this.slideActive.type)
    },
    isShowShowImageTick() {
      if (this.slideActive.type === 'imageChoice' || this.slideActive.type === 'multipleChoice') return false
      return this.slideActive.image && this.slideActive.type != 'pickAnswer'
    },
  },
  mounted() {
    const showQuizInfoTop = cookie.getValue('showQuizInfoTop')
    if (!showQuizInfoTop) {
      cookie.setValue('showQuizInfoTop', new Date().getTime().toString())
    } else if (
      new Date().getTime() - parseInt(showQuizInfoTop, 10) >
      14 * 24 * 60 * 60 * 1000
    ) {
      this.showQuizInfoTop = false
    }
  },
  methods: {
    ...mapActions(['setSlideActiveAction', 'setSyncLoadingAction']),
    updateSettingSlide(type, cb) {
      const params = {
        accessCode: this.$store.state.presentation.uniqueAccessCode,
        socketId: Socket.getSocketId(),
      }
      if (type == 2) {
        params.limitChoice = this.slideActive.limitChoice
      } else if (type == 3) {
        params.hideResult = this.slideActive.hideResult
      } else if (type == 5) {
        params.stopSubmission = this.slideActive.stopSubmission
      } else if (type == 6) {
        params.openEndedMultipleOption = this.slideActive.openEndedMultipleOption
      } else if (type == 7) {
        params.showAllBulletPoints = this.slideActive.showAllBulletPoints
      } else if (type == 8) {
        params.showPercentage = this.slideActive.showPercentage
      } else if (type == 10) {
        params.showQuestionImage = this.slideActive.showQuestionImage
      } else if (type === 16) {
        params.scale = this.slideActive.scale
      } else if (type === 17) {
        params.showVotingResultsOnAudience = this.slideActive.showVotingResultsOnAudience
      } else if (type === 18) {
        params.imageSubmission = this.slideActive.imageSubmission
        EventBus.$emit('on-reDraw-masonry')
      }

      this.setSlideActiveAction(this.getSlideActive)
      params.presenting = this.$store.getters.isPresenting
      this.setSyncLoadingAction(1)
      this._updateSlide(type, params, cb)
    },

    async _updateSlide(type, params, cb) {
      try {
        await SlideApi.updateSlideById(this.slideActive.id, params)
        this._handleAfterUpdateSlide(type, cb)
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
        if (cb) cb(error)
      }
    },

    _handleAfterUpdateSlide(type, cb) {
      const index = this.presentation.slides.findIndex(
        (item) => {
          return item.id == this.slideActive.id
        },
      )
      if (index == -1) return
      if (type == 2) {
        this.presentation.slides[
          index
        ].limitChoice = this.slideActive.limitChoice
        let slideType = this.slideActive.type
        if (slideType === 'ideas') {
          slideType = 'brainstorm'
        }
      } else if (type == 3) {
        this.presentation.slides[
          index
        ].hideResult = this.slideActive.hideResult
        utils.mixpanelTracking(
          'Slide',
          'Edit setting',
          'Presenter edit setting hide result',
          this.slideActive.hideResult,
        )
      } else if (type == 5) {
        this.presentation.slides[
          index
        ].stopSubmission = this.slideActive.stopSubmission
        utils.mixpanelTracking(
          'Slide',
          'Edit setting',
          'Presenter edit setting stop submission',
          this.slideActive.stopSubmission,
        )
      } else if (type == 6) {
        this.presentation.slides[
          index
        ].openEndedMultipleOption = this.slideActive.openEndedMultipleOption
        let slideType = this.slideActive.type
        if (slideType === 'ideas') {
          slideType = 'brainstorm'
        }
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Side right > Edit allow audience to submit more than once',
          `Slide type: ${slideType}, Allow multiple submit: ${this.slideActive.openEndedMultipleOption}`,
        )
      } else if (type == 7) {
        this.presentation.slides[
          index
        ].showAllBulletPoints = this.slideActive.showAllBulletPoints
        utils.mixpanelTracking(
          'Slide',
          'Edit setting',
          'Presenter edit setting open ended multiple times',
        )
      } else if (type == 8) {
        this.presentation.slides[
          index
        ].showPercentage = this.slideActive.showPercentage
        utils.mixpanelTracking(
          'Slide',
          'Edit setting',
          'Presenter edit setting show percentage on chart',
        )
      } else if (type == 10) {
        this.presentation.slides[
          index
        ].showQuestionImage = this.slideActive.showQuestionImage
        utils.mixpanelTracking(
          'Slide',
          'Edit setting',
          'Presenter edit setting show image',
        )
      } else if (type === 16) {
        const status = this.slideActive.scale.mustRate ? 'True' : 'False'
        utils.mixpanelTracking(
          'Slide > Content Tab > Must rate',
          `Must rate: ${status}`,
          'Presenter check/uncheck Must rate box',
        )
      } else if (type === 17) {
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Side right > Content tab > Other settings > Show results on audience\'s phones',
          `Show results: ${this.slideActive.showVotingResultsOnAudience}`,
        )
      } else if (type === 18) {
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Side right > Content tab > Result settings > Image submission',
          `Image submission: ${this.slideActive.imageSubmission}`,
        )
      }
      this.setSyncLoadingAction(2)
      if (cb) cb()
    },

    async handleUpdateFilteringProfanity() {
      try {
        await PresentationApi.updatePresentationByPresentationId(this.presentation.id, {
          socketId: Socket.getSocketId(),
          filteringProfanity: this.presentation.filteringProfanity,
        })
        utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR,
          'Other settings',
          `${this.presentation.filteringProfanity} ? 'Filter on' : 'Filter off'`)
      } catch (error) {
        Sentry.captureException(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.result-settings-wrapper {
  margin-bottom: 16px;
}
.result-settings-option:last-child {
  margin-bottom: 8px;
}
</style>
